import TrackingStore from '../tracking/tracking-store';
import { getEnv, getRoot } from 'mobx-easy';
import { computed, observable, action, when, makeObservable } from 'mobx';
import { RootEnv } from '../../helpers/create-store';
import { isString as _isString, noop as _noop, isNil as _isNil } from 'lodash';
import {
	DriverActivityUpdate,
	GoogleTravelMode,
	LocationUpdate,
	RealTimeEvents,
	SendNoteParams
} from '../../types/shared-location.types';
import RootStore from '../root-store';
import { SharedLocationState } from '../shared-location/shared-location-store';
import {
	OrderTrackingStatus,
	getTrackingStatus,
	getTrackingStatusByLinkedTaskStatus
} from '../../services/order-tracking-service';
import { Task, TaskStatus, TaskTypeId } from '@bringg/types';
import EtaService from '../../services/eta-service';

const ETA_THRESHOLD_MS = 1000 * 60 * 10;
const LOCATION_UPDATE_INTERVAL_THRESHOLD_MS = ETA_THRESHOLD_MS / 2;

class DriverTrackingStore extends TrackingStore {
	private realtimeDriverLocation: google.maps.LatLngLiteral = null;

	private realTimeTravelMode: GoogleTravelMode = null;

	private maskedPhone: string = null;

	private taskStatus: TaskStatus = null;
	private taskType: TaskTypeId = null;

	trackingStatus: OrderTrackingStatus = null;

	private lastLocationUpdateTimestamp = null;
	private locationUpdateIssueLogged = false;

	private wayPointDoneReactionDisposer: () => void = _noop;

	constructor() {
		super();

		makeObservable<
			DriverTrackingStore,
			| 'realtimeDriverLocation'
			| 'realTimeTravelMode'
			| 'maskedPhone'
			| 'trackingStatus'
			| 'taskStatus'
			| 'taskType'
		>(this, {
			realtimeDriverLocation: observable,
			realTimeTravelMode: observable,
			maskedPhone: observable,
			trackingStatus: observable,
			taskStatus: observable,
			taskType: observable,
			setRealtimeDriverLocation: action,
			setRealtimeTravelMode: action,
			setMaskedPhone: action,
			setTrackingStatus: action,
			driverLocation: computed,
			driverTravelMode: computed
		});
	}

	setTrackingStatus(trackingStatus: OrderTrackingStatus) {
		this.trackingStatus = trackingStatus;
	}

	setRealtimeDriverLocation(realtimeDriverLocation: google.maps.LatLngLiteral) {
		this.realtimeDriverLocation = realtimeDriverLocation;
	}

	setRealtimeTravelMode(realTimeTravelMode: GoogleTravelMode) {
		this.realTimeTravelMode = realTimeTravelMode;
	}

	setMaskedPhone(maskedPhone: string) {
		this.maskedPhone = maskedPhone;
	}

	get driverLocation(): google.maps.LatLngLiteral {
		if (this.realtimeDriverLocation) {
			return this.realtimeDriverLocation;
		}

		return { lat: this.customerExperience.current_lat, lng: this.customerExperience.current_lng };
	}

	get driverTravelMode(): GoogleTravelMode {
		const { convertDriverActivityToTravelMode } = getEnv<RootEnv>().etaService;

		return this.realTimeTravelMode
			? this.realTimeTravelMode
			: convertDriverActivityToTravelMode(
					this.customerExperience.driver_activity ? Number(this.customerExperience.driver_activity) : null
				);
	}

	get maskPhoneNumber(): string {
		return this.maskedPhone;
	}

	sendNote = async (note: string) => {
		try {
			const { sharedLocationService } = getEnv<RootEnv>();
			const { note_token, shared_location_uuid, isUserAuthenticated } = this.customerExperience;

			const sendNoteParams: SendNoteParams = {
				note,
				token: note_token,
				isUserAuthenticated
			};

			await sharedLocationService.sendNote(sendNoteParams, shared_location_uuid);
		} catch (e) {
			console.error('Failed to send note to driver', e);
		}
	};

	getMaskedPhone = async () => {
		if (_isString(this.maskedPhone)) {
			return this.maskedPhone;
		}

		const { sharedLocationService } = getEnv<RootEnv>();
		const { uuid } = this.customerExperience;

		const maskedPhone = await sharedLocationService.getMaskedPhone(uuid);
		this.setMaskedPhone(maskedPhone);
		return maskedPhone;
	};

	logLocationUpdateIssueToAirbrake = (errorMessage: string) => {
		const { uuid, driver_uuid, order_uuid } = this.customerExperience;
		const { airbrakeService } = getEnv<RootEnv>();

		if (!this.locationUpdateIssueLogged) {
			airbrakeService.notify({
				error: new Error(errorMessage),
				params: {
					sharedLocationUuid: uuid,
					task_id: order_uuid,
					user_id: driver_uuid
				},
				context: {}
			});
			this.locationUpdateIssueLogged = true;
		}
	};

	monitorLocationUpdateInterval = () => {
		if (this.lastLocationUpdateTimestamp) {
			const currentTimeStamp = Date.now();
			const timeSinceLastUpdate = currentTimeStamp - this.lastLocationUpdateTimestamp;
			const etaMs = EtaService.getHowLongFromNow(this.eta) * 60 * 1000;

			if (timeSinceLastUpdate >= LOCATION_UPDATE_INTERVAL_THRESHOLD_MS) {
				let errorMessage = 'Location Update interval. Threshold reached';

				if (etaMs <= ETA_THRESHOLD_MS) {
					errorMessage = 'Location Update interval. Threshold reached and exceeded half of the ETA';
				}

				console.warn(errorMessage);
				this.logLocationUpdateIssueToAirbrake(errorMessage);
			}

			this.lastLocationUpdateTimestamp = currentTimeStamp;
		} else {
			this.lastLocationUpdateTimestamp = Date.now();
		}
	};

	onDriverLocationUpdate = ({ lat, lng }: LocationUpdate) => {
		// we don't have access to merchant_id unfortunately, make BE provide it
		const { merchant_name } = this.customerExperience;

		if (merchant_name === 'Burger King Production') {
			this.monitorLocationUpdateInterval();
		}
		this.setRealtimeDriverLocation({ lat, lng });
	};

	onDriverActivityUpdate = async ({ activity }: DriverActivityUpdate) => {
		const travelMode = getEnv<RootEnv>().etaService.convertDriverActivityToTravelMode(activity);
		this.setRealtimeTravelMode(travelMode);
	};

	onLinkedOrderUpdate = (linkedTask: Task) => {
		if (_isNil(linkedTask.status)) return;

		this.customerExperience.linked_task_status = linkedTask.status;

		const trackingStatus = getTrackingStatusByLinkedTaskStatus(this.taskStatus, this.taskType, linkedTask.status);
		if (_isNil(trackingStatus)) return;

		this.setTrackingStatus(trackingStatus);
	};

	onOrderUpdate = (task: Task) => {
		if (_isNil(task.status)) return;

		const { route, linked_task_status } = this.customerExperience;

		this.taskStatus = task.status;
		this.taskType = task.task_type_id;

		const trackingStatus = getTrackingStatus(task, route, linked_task_status);
		if (_isNil(trackingStatus)) return;

		this.setTrackingStatus(trackingStatus);
	};

	resetInternalTrackedData = () => {
		this.lastLocationUpdateTimestamp = null;
		this.locationUpdateIssueLogged = false;
	};

	startRealtimeTracking = () => {
		const { uuid, driver_uuid, isUserAuthenticated } = this.customerExperience;

		const driverPayload = {
			share_uuid: uuid,
			driver_uuid: driver_uuid,
			is_user_authenticated: isUserAuthenticated
		};

		this.realtimeWatchTracker.watch(RealTimeEvents.WatchDriver, driverPayload);
	};

	subscribeRealTimeEvents = () => {
		this.registerRealTimeListener(RealTimeEvents.DriverLocationUpdate, this.onDriverLocationUpdate);
		this.registerRealTimeListener(RealTimeEvents.DriverActivityUpdate, this.onDriverActivityUpdate);
		this.registerRealTimeListener(RealTimeEvents.OrderUpdated, this.onOrderUpdate);
		this.registerRealTimeListener(RealTimeEvents.LinkedOrderUpdated, this.onLinkedOrderUpdate);
	};

	startReactions = () => {
		this.wayPointDoneReactionDisposer = when(
			() => this.wayPointDone === true,
			() => {
				this.dispose();
				getRoot<RootStore>().sharedLocationStore.setSharedLocationState(SharedLocationState.Expired);
				this.resetInternalTrackedData();
			}
		);
	};

	disposeReactions = () => {
		this.wayPointDoneReactionDisposer();
	};

	initialize() {
		super.initialize();
		this.resetInternalTrackedData();
		this.startRealtimeTracking();
		this.subscribeRealTimeEvents();
		this.startReactions();
	}

	dispose = () => {
		super.dispose();
		this.disposeReactions();
	};
}

export default DriverTrackingStore;
